import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabase/supabaseClient";
import { setUserRole } from "../supabase/auth";
import { orgFindByName, createOrg } from "../services/orgService";
import { joinOrg } from "../services/membershipService";

export default function OnboardingOrg() {
  const navigate = useNavigate();
  const location = useLocation();
  const incomingRole = location.state?.orgRole ? location.state.orgRole.toString().toLowerCase() : null;
  const desiredRole = incomingRole === "coach" ? "coach" : incomingRole === "admin" ? "admin" : "scout";
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("");
  const [tab, setTab] = useState("join"); // "join" or "create"

  // Join existing
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Create new org
  const [orgName, setOrgName] = useState("");
  const [orgType, setOrgType] = useState("");
  const [city, setCity] = useState("");
  //   const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      setUser(data?.session?.user ?? null);
    })();
  }, []);

  const runSearch = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    const { data, error } = await orgFindByName(q.trim());
    if (!error) setResults(data || []);
    setSearching(false);
  };

  const handleSearchInput = (value) => {
    setQuery(value);

    // Clear existing timeout
    if (searchTimeout) clearTimeout(searchTimeout);

    // Debounce search
    const timeout = setTimeout(() => {
      runSearch(value);
    }, 300);
    setSearchTimeout(timeout);
  };

  const handleJoin = async (orgId) => {
    if (!user) return;
    const { data: authData } = await supabase.auth.getUser();
    const currentUserId = authData?.user?.id || user.id;
    if (!currentUserId) {
      setStatus("Join failed: no authenticated user session.");
      return;
    }
    setStatus("Joining org...");
    const { error } = await joinOrg(currentUserId, orgId, desiredRole);
    if (error) {
      setStatus(`Join failed: ${error.message}`);
      return;
    }
    await setUserRole(desiredRole.toString().toLowerCase());
    await supabase.auth.refreshSession();
    navigate("/profile");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user) return;
    const { data: authData } = await supabase.auth.getUser();
    const currentUserId = authData?.user?.id || user.id;
    if (!currentUserId) {
      setStatus("Create failed: no authenticated user session.");
      return;
    }
    setStatus("Creating org...");
    const payload = {
      name: orgName.trim(),
      type: orgType || null,
      city: city || null,
      website: website || null,
      bio: "Create a bio to let others know about your organization.",
    };
    const { data, error } = await createOrg(payload);
    if (error || !data) {
      console.error("Create org failed:", error);
      setStatus(
        `Create failed: ${error?.message || "Unknown error"}` +
        `${error?.code ? ` (code: ${error.code})` : ""}` +
        `${error?.details ? ` — ${error.details}` : ""}`
      );
      return;
    }
    const { error: mErr } = await joinOrg(currentUserId, data.id, "admin");
    if (mErr) {
      console.error("Join org as admin failed:", mErr, "org id:", data.id, "user id:", user.id);
      setStatus(
        `Organization created, but membership failed: ${mErr.message}` +
        `${mErr?.code ? ` (code: ${mErr.code})` : ""}` +
        `${mErr?.details ? ` — ${mErr.details}` : ""}`
      );
      return;
    }
    await setUserRole("admin");
    await supabase.auth.refreshSession();
    navigate("/profile");
  };
  
  return (
    <main className="page">
      <section className="card">
        <h1>Organization Onboarding</h1>
        <p className="auth-subtitle">Join or create an organization</p>

        {/* Tab toggle */}
        <fieldset className="segmented-toggle" style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="radio"
              name="tab"
              value="join"
              checked={tab === "join"}
              onChange={() => { setTab("join"); setResults([]); setQuery(""); }}
            />
            <span>Join existing</span>
          </label>
          <label>
            <input
              type="radio"
              name="tab"
              value="create"
              checked={tab === "create"}
              onChange={() => setTab("create")}
            />
            <span>Create new</span>
          </label>
        </fieldset>

        {/* Join tab */}
        {tab === "join" && (
          <div>
            <div style={{ display: "flex", gap: ".5rem", marginBottom: ".75rem" }}>
              <input
                className="filter-input"
                value={query}
                onChange={(e) => handleSearchInput(e.target.value)}
                placeholder="Search org name..."
              />
              <button className="btn-scout" onClick={() => runSearch()} disabled={searching}>
                {searching ? "..." : "Search"}
              </button>
            </div>
            <div style={{ display: "grid", gap: ".5rem", maxHeight: "200px", overflowY: "auto" }}>
              {(results || []).map((row) => (
                <div key={row.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5rem" }}>
                  <span style={{ flex: 1, fontSize: ".9rem" }}>{row.name}</span>
                  <button className="btn-signup" onClick={() => handleJoin(row.id)}>
                    Join
                  </button>
                </div>
              ))}
              {results && results.length === 0 && query && !searching && (
                <span className="muted">No orgs found</span>
              )}
            </div>
          </div>
        )}

        {/* Create tab */}
        {tab === "create" && (
          <form onSubmit={handleCreate} className="form" style={{ display: "grid", gap: ".5rem" }}>
            <label style={{ marginBottom: 0 }} >
              Name
              <input value={orgName} onChange={(e) => setOrgName(e.target.value)} required style={{ marginTop: ".2rem" }} placeholder="Organization Name" />
            </label>
            <label style={{ marginBottom: 0 }}>
              Type
              <input value={orgType} onChange={(e) => setOrgType(e.target.value)} style={{ marginTop: ".2rem" }} placeholder="Club, Academy, Agency, University, etc. " />
            </label>
            <label style={{ marginBottom: 0 }}>
              City
              <input value={city} onChange={(e) => setCity(e.target.value)} style={{ marginTop: ".2rem" }} />
            </label>
            <label style={{ marginBottom: 0 }}>
              Website (optional)
              <input value={website} onChange={(e) => setWebsite(e.target.value)} style={{ marginTop: ".2rem" }} placeholder="https://example.com" />
            </label>
            <button type="submit" className="btn-primary" style={{ marginTop: ".5rem" }}>Create (Admin role)</button>
          </form>
        )}

        {status && <p className="status-text" style={{ marginTop: ".75rem", fontSize: ".9rem" }}>{status}</p>}
      </section>
    </main>
  );
}
